/* eslint-disable indent */

import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { NotifierMessages, NotifierType } from 'src/common/notifierConstants';
import React, { ReactElement, useState } from 'react';
import { TextFieldLabels, TextFieldNames } from 'src/common/formConstants';

import CloseIcon from '@mui/icons-material/Close';
import { ErrorMessages } from 'src/common/errorMessagesConstants';
import VesisLightIcon from 'src/resources/icons/vesisLight';
import { content } from 'src/common/lang/en-US';
import { isValidEmail } from 'src/utils/userValidationUtils';
import { saveContactUsDetails } from 'src/utils/rest/contactUsRest';
import { showNotifier } from 'src/store/reducers/notifierReducer';
import theme from 'src/theme/theme';
import { useDispatch } from 'react-redux';

const ContactUsModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(false);
    const [invalidFields, setInvalidFields] = useState({
        email: false,
        message: false,
        mobile: false,
        name: false,
    });

    const [formData, setFormData] = useState({
        email: '',
        message: '',
        mobile: '',
        name: '',
    });

    const dispatch = useDispatch();

    const { contactUsModal, aboutPage } = content;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.name === TextFieldNames.MOBILE_NUMBER) {
            const input = e.target.value.replace(/\D/g, '');
            if (input.length <= 10) {
                if (invalidFields.mobile) {
                    setInvalidFields({ ...invalidFields, mobile: false });
                }
                setFormData({ ...formData, [e.target.name]: input });
            }
        } else {
            setInvalidFields({ ...invalidFields, [e.target.name]: false });
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const handleSubmit = async (): Promise<void> => {
        if (handleValidateForm()) {
            dispatch(showNotifier({ message: NotifierMessages.PLEASE_FILL_ALL_FIELDS, type: NotifierType.WARNING }));
            return;
        }
        await saveContactUsDetails(formData.name, formData.email, formData.mobile, setIsLoading, dispatch);
        onClose();
    };

    const handleValidateForm = (): boolean => {
        const { email, message, mobile, name } = formData;

        const newInvalidFields = {
            email: !isValidEmail(email),
            message: !message,
            mobile: mobile.length !== 10,
            name: !name,
        };

        const isInvalid = Object.values(newInvalidFields).some(Boolean);

        setInvalidFields(newInvalidFields);
        return isInvalid;
    };

    const renderLeftSide = (): ReactElement => (
        <Box
            display="grid"
            gap={{ xs: 2, xxl: 4 }}
            gridTemplateColumns={{ md: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)', xs: '1fr' }}
            minWidth="50%"
        >
            <TextField
                error={invalidFields.name}
                fullWidth
                helperText={invalidFields.name ? ErrorMessages.FULL_NAME_REQUIRED : ''}
                label={TextFieldLabels.FULL_NAME}
                name={TextFieldNames.FULL_NAME}
                onChange={handleChange}
                sx={{
                    '& .MuiFormLabel-root': { fontSize: { xs: '0.8rem', xxl: '2rem' } },
                    '& .MuiInputBase-root': { fontSize: { xs: '0.8rem', xxl: '2rem' }, padding: 0 },
                }}
                value={formData.name}
            />
            <TextField
                error={invalidFields.email}
                fullWidth
                helperText={invalidFields.email ? ErrorMessages.ENTER_VALID_EMAIL : ''}
                label={TextFieldLabels.EMAIL}
                name={TextFieldNames.EMAIL}
                onChange={handleChange}
                sx={{
                    '& .MuiFormLabel-root': { fontSize: { xs: '0.8rem', xxl: '2rem' } },
                    '& .MuiInputBase-root': { fontSize: { xs: '0.8rem', xxl: '2rem' }, padding: 0 },
                }}
                type="email"
                value={formData.email}
            />
            <TextField
                error={invalidFields.mobile}
                fullWidth
                helperText={invalidFields.mobile ? ErrorMessages.MOBILE_NUMBER_REQUIRED : ''}
                label={TextFieldLabels.MOBILE_NUMBER}
                name={TextFieldNames.MOBILE_NUMBER}
                onChange={handleChange}
                sx={{
                    '& .MuiFormLabel-root': { fontSize: { xs: '0.8rem', xxl: '2rem' } },
                    '& .MuiInputBase-root': { fontSize: { xs: '0.8rem', xxl: '2rem' }, padding: 0 },
                }}
                type="tel"
                value={formData.mobile}
            />
            <TextField
                error={invalidFields.message}
                fullWidth
                helperText={invalidFields.message ? ErrorMessages.MESSAGE_REQUIRED : ''}
                label={TextFieldLabels.MESSAGE}
                name={TextFieldNames.MESSAGE}
                onChange={handleChange}
                sx={{
                    '& .MuiFormLabel-root': { fontSize: { xs: '0.8rem', xxl: '2rem' } },
                    '& .MuiInputBase-root': { fontSize: { xs: '0.8rem', xxl: '2rem' }, padding: 0 },
                }}
                type="text"
                value={formData.message}
            />
            <Button
                disabled={invalidFields.email || invalidFields.name || invalidFields.mobile}
                fullWidth
                loading={isLoading}
                onClick={handleSubmit}
                sx={{ fontSize: { xs: 18, xxl: 30 }, fontWeight: 500, mt: 2 }}
                variant="contained"
            >
                Submit
            </Button>
        </Box>
    );

    const renderRightSide = (): ReactElement => (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
            minWidth="50%"
            mr={isMobile ? 0 : 2}
            mt={isMobile ? 2 : 0}
        >
            <Box width="100%">
                <Typography mb={2} variant="h5">
                    {contactUsModal.subHeadings.contactDetails}
                </Typography>
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns={{ md: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)', xs: '1fr' }}
                    width="100%"
                >
                    {contactUsModal.contactDetails.map((details) => (
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={isMobile ? 0 : 4}
                            justifyContent="start"
                            key={details.email}
                            sx={{
                                border: '1px solid',
                                borderColor: 'grey.100',
                                borderRadius: 2,
                                p: 2,
                                pl: 4,
                            }}
                            width="100%"
                        >
                            <Avatar alt={contactUsModal.companyName} sx={{ height: { xxl: 64 }, width: { xxl: 64 } }} />
                            <Box
                                alignItems={isMobile ? 'center' : 'start'}
                                display="flex"
                                flexDirection="column"
                                gap={{ xs: 1, xxl: 4 }}
                                justifyContent="center"
                                mr={4}
                                width="90%"
                            >
                                <Typography fontWeight="bold" textAlign="center" variant="h5">
                                    {details.email}
                                </Typography>
                                <Typography color="text.disabled" textAlign="center" variant="caption2_Thin">
                                    {details.phoneOne}
                                </Typography>
                                <Typography color="text.disabled" textAlign="center" variant="caption2_Thin">
                                    {details.phoneTwo}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" mt={{ xs: '1rem', xxl: '3rem' }}>
                <Typography variant="h5">{contactUsModal.subHeadings.address}</Typography>
                <Box display="flex" flexDirection="column" gap={{ xxl: 4 }} mt={{ xs: '2rem', xxl: '3rem' }}>
                    <Typography variant="h7">{contactUsModal.companyName}</Typography>
                    <Typography variant="h8">{aboutPage.subHeader}</Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Dialog fullScreen={isMobile} maxWidth={'xl'} onClose={onClose} open={open}>
            <DialogTitle>
                <Box alignItems="center" display="flex" justifyContent="space-between">
                    <Box alignItems="center" display="flex" flexDirection="row" gap={4}>
                        <VesisLightIcon height={isMobile ? '40' : '58'} width={isMobile ? '48' : '66'} />
                        <Typography variant="subtitle1">{contactUsModal.subHeadings.contactUs}</Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon sx={{ fontSize: { xs: '1.5rem', xxl: '3rem' } }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box
                    display="grid"
                    gap={isMobile ? 2 : 4}
                    gridTemplateColumns={{ lg: '1fr 2px 1fr', md: '1fr 2px 1fr', sm: '1fr 2px 1fr', xs: '1fr' }}
                    minWidth={{ lg: 600, md: 600, sm: 600, xl: 800, xs: '100%' }}
                    my="1rem"
                >
                    {renderLeftSide()}
                    {isMobile ? (
                        <Divider flexItem orientation="horizontal" sx={{ height: '2px', mt: '1rem' }} />
                    ) : (
                        <Divider flexItem orientation="vertical" sx={{ width: '2px' }} />
                    )}
                    {renderRightSide()}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ContactUsModal;
